import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests
    this.client.interceptors.request.use((config) => {
      const { token } = useAuthStore.getState();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(email: string, password: string, fullName: string, companyName?: string) {
    const response = await this.client.post('/auth/register', {
      email,
      password,
      fullName,
      companyName,
    });
    return response.data;
  }

  async login(email: string, password: string) {
    const response = await this.client.post('/auth/login', {
      email,
      password,
    });
    return response.data;
  }

  // User endpoints
  async getProfile() {
    const response = await this.client.get('/users/me');
    return response.data;
  }

  async updateProfile(data: any) {
    const response = await this.client.put('/users/me', data);
    return response.data;
  }

  async getStats() {
    const response = await this.client.get('/users/stats');
    return response.data;
  }

  // Chat endpoints
  async sendMessage(message: string, mode: string) {
    const response = await this.client.post('/chat/message', {
      message,
      mode,
    });
    return response.data;
  }

  async getChatHistory(mode?: string, limit?: number) {
    const response = await this.client.get('/chat/history', {
      params: { mode, limit },
    });
    return response.data;
  }

  async markHelpful(conversationId: string, helpful: boolean) {
    const response = await this.client.post(`/chat/${conversationId}/helpful`, {
      helpful,
    });
    return response.data;
  }

  // Machine endpoints
  async createMachine(data: any) {
    const response = await this.client.post('/machines', data);
    return response.data;
  }

  async getMachines() {
    const response = await this.client.get('/machines');
    return response.data;
  }

  async getMachine(id: string) {
    const response = await this.client.get(`/machines/${id}`);
    return response.data;
  }

  async updateMachine(id: string, data: any) {
    const response = await this.client.put(`/machines/${id}`, data);
    return response.data;
  }

  async deleteMachine(id: string) {
    const response = await this.client.delete(`/machines/${id}`);
    return response.data;
  }

  // Simulation endpoints
  async createSimulation(data: any) {
    const response = await this.client.post('/simulations', data);
    return response.data;
  }

  async getSimulations() {
    const response = await this.client.get('/simulations');
    return response.data;
  }

  async getSimulation(id: string) {
    const response = await this.client.get(`/simulations/${id}`);
    return response.data;
  }

  async deleteSimulation(id: string) {
    const response = await this.client.delete(`/simulations/${id}`);
    return response.data;
  }

  // Subscription endpoints
  async getCurrentSubscription() {
    const response = await this.client.get('/subscriptions/current');
    return response.data;
  }

  async createCheckoutSession(plan: string) {
    const response = await this.client.post('/subscriptions/checkout', { plan });
    return response.data;
  }

  async cancelSubscription() {
    const response = await this.client.post('/subscriptions/cancel');
    return response.data;
  }
  async updateProfile(data: {
    sector?: string;
    companySize?: string;
    state?: string;
    onboardingGoals?: string[];
    onboardingCompleted?: boolean;
  }) {
   const response = await this.client.patch('/users/profile', data);
    return response.data;
  }
}

export const api = new ApiClient();